import env from "@/env";
import {
  buildQrValue,
  groupWorksheetEntries,
} from "@/utils/worksheetLabelQr.js";
import { getProcessWorksheet } from "@/services/processWorksheet";
import { mapActions } from "vuex";

/**
 * Shared logic for the worksheet sticker print pages
 * (WorksheetPrintQR = A4 sheet, WorksheetPrintLabel = label printer).
 *
 * The consuming component must provide:
 *  - `parameters` (mapGetters from its own print-parameters vuex module)
 *  - `updateParameters` (mapActions from the same module)
 */
export default {
  data() {
    return {
      companyName: env.VUE_APP_COMPANY_NAME,
      companyAddress: env.VUE_APP_COMPANY_ADDRESS,
      companyPhone: env.VUE_APP_COMPANY_PHONE,
      id: "",
      processWorksheet: {},
      loaded: false,
      productInfo: null,
      filterTerm: null,
      lmeBrands: [],
      productNames: [],
      isLME: false,
    };
  },
  computed: {
    commodityName() {
      return (
        this.parameters.commodityOverride || this.processWorksheet.productName
      );
    },
    // Group entries by entryGroupId so that FIFO/LIFO split entries
    // produce one sticker per logical entry (1 user input = 1 sticker).
    // Shared with auto-print so both number the labels identically.
    groupedEntries() {
      return groupWorksheetEntries(
        this.processWorksheet && this.processWorksheet.entries
      );
    },
  },
  methods: {
    ...mapActions("processes", ["loadProcessTypes"]),
    ...mapActions("categoryStore", ["fetchCategory"]),
    ...mapActions("messages", ["addErrorMessage"]),

    // Load the worksheet plus its product/LME context; resolves once the
    // stickers are ready to render
    async loadWorksheetInstance() {
      await this.loadProcessTypes();
      try {
        const response = await getProcessWorksheet(this.id);
        this.processWorksheet = response.data;

        await this.loadProductInfo();

        if (
          this.processWorksheet.productName &&
          this.productNames.includes(this.processWorksheet.productName)
        ) {
          const brand = this.lmeBrands.find(
            (b) => b.internalProductName === this.processWorksheet.productName
          );
          if (brand) {
            this.updateParameters({ brandName: brand.lmeBrand });
            this.isLME = true;
          }
        }
      } catch (error) {
        this.addErrorMessage("Error loading processWorksheet");
      } finally {
        this.loaded = true;
      }
    },
    getQrCodeUrl(entry) {
      // Shared with auto-print: a sticker printed when the entry was saved and
      // one reprinted from this page later must scan to the same value.
      return buildQrValue({
        worksheet: this.processWorksheet,
        entry,
        lotIndex: this.entryIndex(entry),
        isLME: this.isLME,
        lmeBrand: this.parameters.brandName || "",
        companyName: this.companyName,
      });
    },
    async getLmeBrandProducts() {
      const response = await this.$axios.get("/api/lme/records/brands");
      if (
        response.data &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        this.productNames = response.data.map(
          (item) => item.internalProductName
        );
        return response.data;
      } else {
        this.productNames = [];
        return [];
      }
    },
    async loadProductInfo() {
      if (this.processWorksheet.productId) {
        return await this.fetchCategory(this.processWorksheet.productId).then(
          (resp) => {
            this.productInfo = resp;

            return this.productInfo;
          }
        );
      } else {
        return null;
      }
    },
    entryIndex(entry) {
      const entries = this.groupedEntries;
      if (entries && entries.length > 0) {
        const index = entries.findIndex((e) => e.id == entry.id);
        if (index >= 0) {
          return index + 1;
        } else {
          return 0;
        }
      }
    },
    parseFilterTerms(terms) {
      const entryIndexes = new Set();
      if (terms) {
        const list = terms.split(",");
        list.forEach((l) => {
          //check if range
          if (l.includes("-")) {
            const range = l.split("-");
            if (range.length == 2) {
              const start = parseInt(range[0].trim());
              const end = parseInt(range[1].trim());
              let cursor = start;
              while (cursor <= end) {
                entryIndexes.add(cursor);
                cursor++;
              }
            }
          } else {
            entryIndexes.add(parseInt(l.trim()));
          }
        });
      }
      return [...entryIndexes];
    },
    addStickerField() {
      let fields = this.parameters.optionalFields;
      if (!fields) {
        fields = [];
      }
      fields.push({ label: `Field ${fields.length + 1} Name`, value: "" });
      this.$set(this.parameters, "optionalFields", fields);
    },
    deleteField(index) {
      let fields = this.parameters.optionalFields;
      if (fields) {
        fields.splice(index, 1);
        this.$set(this.parameters, "optionalFields", fields);
      }
    },
  },
};
